import { IReviewReportSummary } from 'features/report/services/types/review';
import React from 'react';
import styled from 'styled-components';

interface StarInfo {
  key: number;
  isFilled: boolean;
  isHalfFilled: boolean;
}

interface StarRatingChartProps {
  data: IReviewReportSummary | null;
}

const StarRatingChart: React.FC<StarRatingChartProps> = ({ data }) => {
  const defaultRatingCounts = {
    5: 0,
    4: 0,
    3: 0,
    2: 0,
    1: 0,
  };

  const counts = data?.details || defaultRatingCounts;

  const maxCount = Math.max(...Object.values(counts));

  const stars: StarInfo[] = Array(5)
    .fill(0)
    .map((_, index) => {
      const isFilled = index < Math.floor(data?.averageStars || 0);
      const isHalfFilled =
        !isFilled &&
        index < Math.ceil(data?.averageStars || 0) &&
        (data?.averageStars || 0) % 1 !== 0;

      return {
        key: index,
        isFilled,
        isHalfFilled,
      };
    });

  return (
    <ChartContainer>
      <ChartTitle>Average Star Rating</ChartTitle>

      <RatingContainer>
        <RatingValue>
          <MainValue>{data?.averageStars || 0}</MainValue>
          <MaxValue>/5</MaxValue>
        </RatingValue>

        <StarsContainer>
          {stars.map((star) => (
            <StarWrapper key={star.key}>
              {star.isFilled ? (
                <svg viewBox='0 0 24 24' fill='#f97316' stroke='none'>
                  <path d='M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z' />
                </svg>
              ) : star.isHalfFilled ? (
                <svg viewBox='0 0 24 24' fill='#f97316' stroke='none'>
                  <path
                    d='M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z'
                    fillOpacity='0.5'
                  />
                  <path d='M12 17.27V2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z' />
                </svg>
              ) : (
                <svg viewBox='0 0 24 24' fill='#e5e7eb' stroke='none'>
                  <path d='M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z' />
                </svg>
              )}
            </StarWrapper>
          ))}
        </StarsContainer>
      </RatingContainer>

      <DistributionContainer>
        {Object.entries(counts)
          .sort((a, b) => Number(b[0]) - Number(a[0]))
          .map(([stars, count]) => (
            <DistributionRow key={stars}>
              <DistributionLabel>{stars} stars</DistributionLabel>
              <BarBackground>
                <BarFill width={(count / maxCount) * 100} />
              </BarBackground>
            </DistributionRow>
          ))}
      </DistributionContainer>
    </ChartContainer>
  );
};

export default StarRatingChart;

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const ChartTitle = styled.h2`
  font-size: 26px;
  font-weight: 400;
  color: #1e293b;
  margin: 0 0 24px 0;
  text-transform: capitalize;
`;

const RatingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
`;

const RatingValue = styled.div`
  display: flex;
  align-items: baseline;
`;

const MainValue = styled.span`
  font-size: 48px;
  font-weight: bold;
  color: #1e293b;
`;

const MaxValue = styled.span`
  font-size: 22px;
  color: #64748b;
  margin-left: 4px;
`;

const StarsContainer = styled.div`
  display: flex;
  margin-top: 8px;
`;

const StarWrapper = styled.div`
  width: 32px;
  height: 32px;
  margin: 0 4px;
`;

const DistributionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DistributionRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const DistributionLabel = styled.span`
  text-align: right;
  margin-right: 8px;
  font-size: 18px;
  color: #64748b;
  text-transform: capitalize;
`;

const BarBackground = styled.div`
  flex: 1;
  height: 10px;
  background-color: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
`;

const BarFill = styled.div<{ width: number }>`
  height: 100%;
  width: ${(props) => props.width}%;
  background-color: #f97316;
  border-radius: 9999px;
`;
